import { useCallback, useLayoutEffect, useRef } from 'react'

const SEPARATOR_CLASS = 'with-separator'

// Shows the comma between a tee time and its starting hole on the cells where
// the two sit on one line, and hides it on the cells where the hole has wrapped
// underneath - there it would leave the time ending in a dangling comma.
const useThruSeparators = (tableRef) => {
  const lastWidthRef = useRef(null)

  const apply = useCallback(() => {
    const table = tableRef.current

    if (!table) {
      return
    }

    const times = table.querySelectorAll('.lbv2__thru-time')

    if (!times.length) {
      return
    }

    const decisions = []

    times.forEach((time) => {
      const hole = time.parentElement && time.parentElement.querySelector('.lbv2__thru-hole')

      if (!hole) {
        decisions.push([ time, false ])
        return
      }

      const a = time.getBoundingClientRect()
      const b = hole.getBoundingClientRect()

      // Vertical overlap, not equal tops: the two spans share a line without
      // sharing a box top, so an equality test is never true.
      decisions.push([ time, a.bottom > b.top && b.bottom > a.top ])
    })

    decisions.forEach(([ time, sameLine ]) => time.classList.toggle(SEPARATOR_CLASS, sameLine))
  }, [ tableRef ])

  // After every render: the rows themselves may have changed, and polling
  // replaces thru values without touching the table's size.
  useLayoutEffect(apply)

  useLayoutEffect(() => {
    const table = tableRef.current

    if (!table) {
      return undefined
    }

    const observer = new ResizeObserver((entries) => {
      const width = entries[0] && entries[0].contentRect.width

      // Height churn is constant here - rows expanding, scorecards opening -
      // and none of it can change where a line breaks.
      if (width === lastWidthRef.current) {
        return
      }

      lastWidthRef.current = width
      apply()
    })

    observer.observe(table)

    return () => observer.disconnect()
  }, [ tableRef, apply ])
}

export default useThruSeparators
